import { Send } from "lucide-react";
import { type KeyboardEvent, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

interface ChatInputProps {
	onSend: (message: string) => void;
	isLoading: boolean;
}

export function ChatInput({ onSend, isLoading }: ChatInputProps) {
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const handleSend = useCallback(() => {
		const text = textareaRef.current?.value.trim();
		if (!text || isLoading) return;
		onSend(text);
		if (textareaRef.current) {
			textareaRef.current.value = "";
			textareaRef.current.style.height = "auto";
		}
	}, [onSend, isLoading]);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === "Enter" && !e.shiftKey) {
				e.preventDefault();
				handleSend();
			}
		},
		[handleSend],
	);

	const handleInput = useCallback(() => {
		const el = textareaRef.current;
		if (!el) return;
		el.style.height = "auto";
		el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
	}, []);

	return (
		<div className="flex items-end gap-2 border-t p-3">
			<Textarea
				ref={textareaRef}
				placeholder="Search therapists... e.g. 'female therapist in Jakarta Selatan next Monday 2pm'"
				className="min-h-[40px] max-h-[120px] resize-none"
				rows={1}
				onKeyDown={handleKeyDown}
				onInput={handleInput}
				disabled={isLoading}
			/>
			<Button
				size="icon"
				onClick={handleSend}
				disabled={isLoading}
				className="shrink-0"
			>
				<Send className="size-4" />
			</Button>
		</div>
	);
}
